/**
 * 日程编辑器的表单状态和计算（与 iOS EventEditView 一致），都是纯函数，方便单元测试。
 * 时间都按家庭时区（LocalCalendar）计算；日期 YYYY-MM-DD，时刻 HH:mm。
 */
import { defaultTask, eventTimeMode, ON_TIME_BONUS, type EventAction, type LocalCalendar, type ReminderRule, type ScheduleEvent, type TimeMode } from "../../core";
import { buildRRule, NO_REPEAT, parseRepeat, type RepeatRule } from "./repeat";

/** 一个日程最多 14 天（与服务端 MAX_EVENT_DAYS 一致） */
export const MAX_DAYS = 14;

export const DEFAULT_SPEAK = "{title}要开始啦";

/** 可选的提前时间（分钟，负数表示提前） */
export const OFFSET_CHOICES = [0, -5, -10, -15, -30, -60] as const;
export const SOUNDS: readonly (readonly [string, string])[] = [
  ["chime_soft", "柔和"],
  ["bell", "铃声"],
  ["marimba", "木琴"],
];
export const REPEAT_INTERVALS = [1, 2, 3, 5, 10] as const;
export const REPEAT_MAXES = [0, 1, 2, 3, 5] as const;
export const ESCALATE_CHOICES = [3, 5, 10, 15, 30] as const;

export const TIME_MODE_CHOICES = [
  ["all_day", "全天"],
  ["range", "时段"],
  ["due", "截止"],
  ["point", "到点"],
] as const satisfies readonly (readonly [TimeMode, string])[];

/** 打开其它 App 的常用列表（iPad 用链接打开，安卓用包名打开）；其它的选"自定义"自己填 */
export const APP_PRESETS: readonly { app: string; name: string; iosUrl: string; androidPackage: string }[] = [
  { app: "ximalaya", name: "喜马拉雅", iosUrl: "iting://", androidPackage: "com.ximalaya.ting.android" },
  { app: "bilibili", name: "哔哩哔哩", iosUrl: "bilibili://", androidPackage: "tv.danmaku.bili" },
  { app: "tencent_video", name: "腾讯视频", iosUrl: "tenvideo://", androidPackage: "com.tencent.qqlive" },
  { app: "iqiyi", name: "爱奇艺", iosUrl: "iqiyi://", androidPackage: "com.qiyi.video" },
  { app: "youku", name: "优酷", iosUrl: "youku://", androidPackage: "com.youku.phone" },
  { app: "qqmusic", name: "QQ音乐", iosUrl: "qqmusic://", androidPackage: "com.tencent.qqmusic" },
  { app: "netease_music", name: "网易云音乐", iosUrl: "orpheus://", androidPackage: "com.netease.cloudmusic" },
  { app: "wechat", name: "微信", iosUrl: "weixin://", androidPackage: "com.tencent.mm" },
];

export interface EventForm {
  title: string;
  category: string;
  icon: string;
  mode: TimeMode;
  startDate: string;
  /** 开始时刻；截止 = 截止时间，到点 = 那个时间（全天时不用，保留给改回时段用） */
  startTime: string;
  /** 结束那天：跨天时晚于 startDate；全天时是最后一天 */
  endDate: string;
  endTime: string;
  repeat: RepeatRule;
  untilDate: string | null;
  exdates: string[];
  location: string;
  notes: string;
  /** 任务做完、家长确认后加的分 */
  points: number;
  /** 学习内容：截止前（截止 = 那个时间，时段 = 结束时）做完另加的分 */
  bonus: number;
  /** 打开什么，null 什么都不打开 */
  action: EventAction | null;
  /** 任务（孩子点"做完了"、家长确认加分）还是只提醒 */
  task: boolean;
  /** 家长动过任务开关（或是已有日程）：之后不再跟着时间类型 / 打开什么改默认值 */
  taskTouched: boolean;
}

export interface ReminderForm {
  on: boolean;
  offsets: number[];
  speakText: string;
  sound: string;
  requireAck: boolean;
  repeatInterval: number;
  repeatMax: number;
  escalate: number | null;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** 打开编辑器时的表单：已有日程读回；新建时从下一个整点开始、1 小时 */
export function initialForm(cal: LocalCalendar, event: ScheduleEvent | null | undefined, date: string, now = Date.now()): EventForm {
  if (!event) {
    const hour = Math.min(22, cal.parts(now).hour + 1);
    return {
      title: "",
      category: "after_school",
      icon: "star",
      mode: "range",
      startDate: date,
      startTime: `${pad(hour)}:00`,
      endDate: date,
      endTime: `${pad(hour + 1)}:00`,
      repeat: NO_REPEAT,
      untilDate: null,
      exdates: [],
      location: "",
      notes: "",
      points: 1,
      bonus: ON_TIME_BONUS,
      action: null,
      task: defaultTask("range", null),
      taskTouched: false,
    };
  }
  const base = {
    title: event.title,
    category: event.category,
    icon: event.icon,
    startDate: event.startDate,
    repeat: parseRepeat(event.rrule, cal.weekday(event.startDate)),
    untilDate: event.untilDate ?? null,
    exdates: [...(event.exdates ?? [])],
    location: event.location ?? "",
    notes: event.notes ?? "",
    points: event.pointsReward ?? 1,
    bonus: event.onTimeBonus ?? ON_TIME_BONUS,
    action: event.action ?? null,
    task: event.action?.type === "content" || event.task !== false,
    taskTouched: true,
  };
  const mode = eventTimeMode(event);
  if (mode === "all_day") {
    // 全天：结束日期 = 最后一天；时间先放 9–10 点，改回时段时用
    const days = Math.max(1, Math.round(event.durationMin / 1440));
    return { ...base, mode, startTime: "09:00", endDate: cal.addDays(event.startDate, days - 1), endTime: "10:00" };
  }
  const startAt = cal.ms(event.startDate, event.startTime) ?? 0;
  // 截止 / 到点没有结束：先放 1 小时，改成时段时用
  const endAt = startAt + (mode === "range" ? event.durationMin : 60) * 60_000;
  return { ...base, mode, startTime: event.startTime, endDate: cal.dateString(endAt), endTime: cal.timeString(endAt) };
}

/** 全天：天数 × 1440；截止 / 到点：0；时段：结束时刻 − 开始时刻（可以跨天）。时间不合法时返回 0 */
export function durationMin(cal: LocalCalendar, f: EventForm): number {
  if (f.mode === "all_day") return (cal.daysBetween(f.startDate, f.endDate) + 1) * 1440;
  if (f.mode === "due" || f.mode === "point") return 0;
  const a = cal.ms(f.startDate, f.startTime);
  const b = cal.ms(f.endDate, f.endTime);
  if (a == null || b == null) return 0;
  return Math.round((b - a) / 60_000);
}

/** 时间有问题时的提示（不能保存），没问题返回 null */
export function timeProblem(cal: LocalCalendar, f: EventForm): string | null {
  if (f.mode === "due" || f.mode === "point") return null;
  const d = durationMin(cal, f);
  if (f.mode === "all_day") {
    if (f.endDate < f.startDate) return "结束日期不能早于开始日期";
  } else if (d <= 0) {
    return "结束时间要晚于开始时间";
  }
  if (d > MAX_DAYS * 1440) return `一个日程最多 ${MAX_DAYS} 天`;
  return null;
}

/** 跨天 / 多天时提示一共多久："全天 · 共 3 天"、"跨天 · 共 1 天 2 小时 30 分钟" */
export function spanHint(cal: LocalCalendar, f: EventForm): string | null {
  if (f.mode === "due" || f.mode === "point") return null;
  if (f.mode === "all_day") {
    const n = cal.daysBetween(f.startDate, f.endDate) + 1;
    return n > 1 ? `全天 · 共 ${n} 天` : null;
  }
  if (f.endDate === f.startDate) return null;
  const m = durationMin(cal, f);
  if (m <= 0) return null;
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  const mm = m % 60;
  const text = [d > 0 ? `${d} 天` : null, h > 0 ? `${h} 小时` : null, mm > 0 ? `${mm} 分钟` : null].filter(Boolean).join(" ");
  return `跨天 · 共 ${text}`;
}

/** 改开始日期：结束日期跟着平移，保持原来的天数 */
export function changeStartDate(cal: LocalCalendar, f: EventForm, next: string): EventForm {
  if (!next || next === f.startDate) return f;
  return { ...f, startDate: next, endDate: cal.addDays(f.endDate, cal.daysBetween(f.startDate, next)) };
}

/** 切换时间类型：改成时段时如果结束不晚于开始，默认 1 小时 */
export function changeMode(cal: LocalCalendar, f: EventForm, mode: TimeMode): EventForm {
  const next = withTaskDefault({ ...f, mode });
  if (mode !== "range" || durationMin(cal, next) > 0) return next;
  const a = cal.ms(f.startDate, f.startTime);
  if (a == null) return next;
  const e = a + 3_600_000;
  return { ...next, endDate: cal.dateString(e), endTime: cal.timeString(e) };
}

/** 结束日期可选范围：开始日期 ~ 开始日期 + 14 天 */
export function endDateRange(cal: LocalCalendar, f: EventForm): [string, string] {
  return [f.startDate, cal.addDays(f.startDate, MAX_DAYS)];
}

/** 打开什么没填完整时的提示，没问题返回 null */
export function actionProblem(a: EventAction | null): string | null {
  if (!a) return null;
  if (a.type === "content") return a.contentId ? null : "选一个学习内容";
  if (!a.name.trim()) return "填一下 App 名称";
  if (!a.iosUrl?.trim() && !a.androidPackage?.trim()) return "iPad 链接和安卓包名至少填一个";
  if (a.iosUrl?.trim() && !/^[a-z][a-z0-9+.-]*:/i.test(a.iosUrl.trim())) return "iPad 链接要像 weixin:// 这样";
  if (a.androidPackage?.trim() && !/^[a-zA-Z]\w*(\.[a-zA-Z]\w*)+$/.test(a.androidPackage.trim())) return "安卓包名要像 com.tencent.mm 这样";
  return null;
}

/** 换"打开什么"：学习内容的奖励默认 10，其它默认 1（家长没改过时跟着换） */
export function changeAction(f: EventForm, action: EventAction | null): EventForm {
  const wasContent = f.action?.type === "content";
  const isContent = action?.type === "content";
  let points = f.points;
  if (isContent && !wasContent && points === 1) points = 10;
  if (!isContent && wasContent && points === 10) points = 1;
  return withTaskDefault({ ...f, action, points });
}

/** 学习内容固定是任务；新建、家长还没动过开关时跟着时间类型和打开什么给默认值 */
function withTaskDefault(f: EventForm): EventForm {
  if (f.action?.type === "content") return { ...f, task: true };
  return f.taskTouched ? f : { ...f, task: defaultTask(f.mode, f.action) };
}

/** 家长拨任务开关（学习内容不能改成只提醒） */
export function changeTask(f: EventForm, task: boolean): EventForm {
  if (f.action?.type === "content") return f;
  return { ...f, task, taskTouched: true };
}

export function canSave(cal: LocalCalendar, f: EventForm): boolean {
  return f.title.trim().length > 0 && timeProblem(cal, f) == null && actionProblem(f.action) == null;
}

function actionBody(a: EventAction | null) {
  if (!a) return null;
  if (a.type === "content") return { type: "content", contentId: a.contentId };
  const trim = (v: string | null | undefined) => (v && v.trim() ? v.trim() : undefined);
  return { type: "app", name: a.name.trim(), app: trim(a.app), iosUrl: trim(a.iosUrl), androidPackage: trim(a.androidPackage) };
}

/** POST children/{cid}/events、PATCH events/{id} 的请求体：可空字段显式写 null（PATCH 时才能清空） */
export function eventBody(cal: LocalCalendar, f: EventForm) {
  const repeating = f.repeat.kind !== "none";
  return {
    category: f.category,
    title: f.title.trim(),
    icon: f.icon,
    location: f.location.trim() ? f.location.trim() : null,
    notes: f.notes.trim() ? f.notes.trim() : null,
    startDate: f.startDate,
    timeMode: f.mode,
    startTime: f.mode === "all_day" ? "00:00" : f.startTime,
    durationMin: durationMin(cal, f),
    allDay: f.mode === "all_day",
    action: actionBody(f.action),
    rrule: buildRRule(f.repeat, f.startDate),
    untilDate: repeating ? f.untilDate : null,
    exdates: repeating ? [...f.exdates].sort() : [],
    task: f.task || f.action?.type === "content",
    pointsReward: f.points,
    ...(f.action?.type === "content" ? { onTimeBonus: f.bonus } : {}),
  };
}

/** 有按时完成奖励：学习内容、有截止时间（截止 / 时段） */
export function hasOnTimeBonus(f: Pick<EventForm, "action" | "mode">): boolean {
  return f.action?.type === "content" && (f.mode === "due" || f.mode === "range");
}

/** 新建日程：默认开提醒；已有日程：有提醒规则就读回，没有就关着 */
export function initialReminder(event: ScheduleEvent | null | undefined, rule: ReminderRule | null | undefined): ReminderForm {
  const defaults: ReminderForm = { on: !event, offsets: [-10], speakText: DEFAULT_SPEAK, sound: "chime_soft", requireAck: true, repeatInterval: 2, repeatMax: 3, escalate: null };
  if (!event || !rule) return defaults;
  return {
    on: rule.enabled,
    offsets: [...rule.offsetsMin],
    speakText: rule.speakText,
    sound: rule.sound,
    requireAck: rule.requireAck,
    repeatInterval: rule.repeatIntervalMin ?? 2,
    repeatMax: rule.repeatMax ?? 3,
    escalate: rule.escalateAfterMin ?? null,
  };
}

/** 保存后要不要有提醒规则 */
export function wantsRule(r: ReminderForm): boolean {
  return r.on && r.offsets.length > 0;
}

/** POST children/{cid}/reminder-rules、PATCH reminder-rules/{id} 的请求体 */
export function ruleBody(eventId: string, r: ReminderForm) {
  return {
    sourceType: "event",
    sourceId: eventId,
    offsetsMin: [...r.offsets].sort((a, b) => a - b),
    speakText: r.speakText.trim() ? r.speakText : DEFAULT_SPEAK,
    sound: r.sound,
    requireAck: r.requireAck,
    repeatIntervalMin: r.repeatInterval,
    repeatMax: r.requireAck ? r.repeatMax : 0,
    escalateAfterMin: r.requireAck ? r.escalate : null,
    enabled: true,
  };
}

/** 有效期文字："长期" / "至 10月30日 · 2 天除外" */
export function validityText(cal: LocalCalendar, f: EventForm): string {
  let s = f.untilDate ? `至 ${cal.monthDay(f.untilDate)}` : "长期";
  if (f.exdates.length) s += ` · ${f.exdates.length} 天除外`;
  return s;
}
